import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Activity, RefreshCw, DollarSign, TrendingUp, Clock, Zap,
  AlertCircle, CheckCircle2, Loader2, MapPin, Calendar, X, Ban,
} from "lucide-react";

const fmtUSD = (n) => `$${(Number(n) || 0).toFixed(4)}`;
const fmtPct = (n) => `${((Number(n) || 0) * 100).toFixed(1)}%`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit",
                                       hour: "2-digit", minute: "2-digit" });
  } catch { return iso.slice(0, 16); }
};

const statusStyle = {
  en_curso: { icon: Loader2, cls: "bg-amber-50 text-amber-800 border-amber-200", spin: true, label: "En curso" },
  completada: { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Completada" },
  error: { icon: AlertCircle, cls: "bg-red-50 text-red-800 border-red-200", label: "Error" },
  cancelada: { icon: Ban, cls: "bg-slate-100 text-slate-700 border-slate-300", label: "Cancelada" },
};

export default function RadarActividad() {
  const [resumen, setResumen] = useState(null);
  const [capturas, setCapturas] = useState([]);
  const [scheduler, setScheduler] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [cancellingId, setCancellingId] = useState(null);

  const cancelCaptura = async (capturaId) => {
    if (!window.confirm(
      "¿Cancelar esta búsqueda?\n\nSe aborta el/los run(s) de Apify (dejamos de pagar) y se registra el costo consumido hasta el abort."
    )) return;
    setCancellingId(capturaId);
    try {
      const res = await api.post(`/opportunities/captura/${capturaId}/cancel`);
      const cost = res.data?.costo_hasta_abort_usd ?? 0;
      const n = (res.data?.runs_aborted || []).length;
      toast.success(`Cancelada · ${n} run(s) Apify abortados · costo hasta abort $${Number(cost).toFixed(4)}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "No pudimos cancelar");
    } finally {
      setCancellingId(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [r, c, s] = await Promise.all([
        api.get(`/actividad/resumen?days=${days}`),
        api.get("/actividad/capturas?limit=50"),
        api.get("/actividad/scheduler"),
      ]);
      setResumen(r.data);
      setCapturas(c.data.items || []);
      setScheduler(s.data);
    } catch (e) {
      toast.error("Error cargando actividad");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  return (
    <div className="p-8 lg:p-12" data-testid="radar-actividad-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black text-slate-900 tracking-tight">
              Actividad
            </h1>
            <p className="text-sm text-slate-500 mt-2">
              Historial de búsquedas, costos y rendimiento por fuente
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              data-testid="days-selector"
              value={days} onChange={(e) => setDays(Number(e.target.value))}
              className="h-10 px-3 border border-slate-300 rounded-none text-sm bg-white"
            >
              <option value={7}>Últimos 7 días</option>
              <option value={30}>Últimos 30 días</option>
              <option value={90}>Últimos 90 días</option>
            </select>
            <Button variant="outline" onClick={load} className="rounded-none border-slate-300">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {/* KPIs top */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <KpiCard
            icon={<Activity className="w-4 h-4" />}
            label="Búsquedas"
            value={loading ? "…" : (resumen?.total_capturas ?? 0)}
            testid="kpi-total-capturas"
          />
          <KpiCard
            icon={<DollarSign className="w-4 h-4" />}
            label="Costo total"
            value={loading ? "…" : fmtUSD(resumen?.total_costo_usd)}
            testid="kpi-total-costo"
          />
          <KpiCard
            icon={<TrendingUp className="w-4 h-4" />}
            label="Yield promedio Zonaprop"
            value={loading ? "…" : fmtPct(resumen?.por_fuente?.zonaprop?.avg_ratio_util)}
            testid="kpi-yield-zonaprop"
          />
          <KpiCard
            icon={<TrendingUp className="w-4 h-4" />}
            label="Yield promedio Properati"
            value={loading ? "…" : fmtPct(resumen?.por_fuente?.properati?.avg_ratio_util)}
            testid="kpi-yield-properati"
          />
        </div>

        {/* Scheduler status */}
        <div className="bg-white border border-slate-200 mb-6" data-testid="scheduler-panel">
          <div className="p-4 flex flex-wrap items-center gap-4">
            <div className={`w-2 h-2 rounded-full ${scheduler?.running ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`} />
            <div className="flex-1 min-w-[220px]">
              <p className="font-heading text-sm font-bold text-slate-900">
                Búsquedas automáticas · {scheduler?.running ? "activas" : "detenidas"}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                {scheduler?.zonas_suscritas > 0
                  ? <>Corren todos los días a las {String(scheduler?.hour ?? 6).padStart(2, "0")}:{String(scheduler?.minute ?? 0).padStart(2, "0")} sobre <strong>{scheduler.zonas_suscritas} zona{scheduler.zonas_suscritas === 1 ? "" : "s"}</strong>. Próxima corrida: {fmtDate(scheduler?.next_run)}.</>
                  : "No hay zonas con búsqueda automática activada. Activá el toggle en cualquier zona desde el mapa."}
              </p>
            </div>
            <Link to="/radar" className="text-xs text-emerald-800 hover:underline">
              Ir al mapa →
            </Link>
          </div>
        </div>

        {/* Rendimiento por fuente */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {["zonaprop", "properati"].map((fid) => {
            const pf = resumen?.por_fuente?.[fid];
            return (
              <div key={fid} className="bg-white border border-slate-200 p-5" data-testid={`fuente-card-${fid}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading text-base font-bold text-slate-900 capitalize">{fid}</h3>
                  <Zap className="w-4 h-4 text-slate-400" />
                </div>
                {pf ? (
                  <div className="grid grid-cols-2 gap-y-2 text-xs">
                    <span className="text-slate-500">Corridas</span>
                    <span className="text-right font-mono">{pf.capturas}</span>
                    <span className="text-slate-500">Total raw</span>
                    <span className="text-right font-mono">{pf.total_raw}</span>
                    <span className="text-slate-500">Útiles (en zona)</span>
                    <span className="text-right font-mono">{pf.total_dentro_zona}</span>
                    <span className="text-slate-500">Yield promedio</span>
                    <span className="text-right font-mono font-bold text-emerald-800">{fmtPct(pf.avg_ratio_util)}</span>
                    <span className="text-slate-500">Costo total</span>
                    <span className="text-right font-mono">{fmtUSD(pf.total_usd)}</span>
                    <span className="text-slate-500">Costo por útil</span>
                    <span className="text-right font-mono">{pf.costo_por_util_usd != null ? fmtUSD(pf.costo_por_util_usd) : "—"}</span>
                  </div>
                ) : <p className="text-xs text-slate-400">Sin datos en este período</p>}
              </div>
            );
          })}
        </div>

        {/* Tabla capturas */}
        <div className="bg-white border border-slate-200" data-testid="capturas-table">
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-heading text-sm font-bold text-slate-900">Historial de búsquedas</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Fecha</th>
                  <th className="text-left px-4 py-3 font-medium">Zona</th>
                  <th className="text-left px-4 py-3 font-medium">Trigger</th>
                  <th className="text-right px-4 py-3 font-medium">Raw · útiles</th>
                  <th className="text-right px-4 py-3 font-medium">Yield</th>
                  <th className="text-right px-4 py-3 font-medium">Duración</th>
                  <th className="text-right px-4 py-3 font-medium">Costo</th>
                  <th className="text-right px-4 py-3 font-medium">Dupes X</th>
                  <th className="text-left px-4 py-3 font-medium">Estado</th>
                </tr>
              </thead>
              <tbody>
                {loading && [1, 2, 3].map((i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td colSpan={9} className="px-4 py-3"><Skeleton className="h-4 w-full" /></td>
                  </tr>
                ))}
                {!loading && capturas.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">Sin búsquedas aún</td></tr>
                )}
                {!loading && capturas.map((c) => {
                  const r = c.resultado || {};
                  const ypf = r.yield_por_fuente || {};
                  const totalRaw = Object.values(ypf).reduce((a, x) => a + (x.raw || 0), 0);
                  const totalUtil = Object.values(ypf).reduce((a, x) => a + (x.dentro_zona || 0), 0);
                  const yieldAvg = totalRaw > 0 ? totalUtil / totalRaw : 0;
                  const dur = c.started_at && c.completed_at
                    ? Math.round((new Date(c.completed_at) - new Date(c.started_at)) / 1000) : null;
                  const st = statusStyle[c.status] || statusStyle.en_curso;
                  const StIcon = st.icon;
                  const coordsActor = r.coords_desde_actor ?? 0;
                  const geocod = r.geocodificados ?? 0;
                  const outside = r.outside_polygon ?? 0;
                  const sinUbic = r.sin_ubicacion ?? 0;
                  const aprox = r.aprox_incluidos ?? 0;
                  const lossBreakdown =
                    `Diagnóstico:\n` +
                    `· Raw: ${totalRaw}\n` +
                    `· Coords desde actor: ${coordsActor}\n` +
                    `· Geocodificados (Nominatim): ${geocod}\n` +
                    `· Dentro del polígono: ${totalUtil}\n` +
                    `· Fuera del polígono: ${outside}\n` +
                    `· Sin ubicación: ${sinUbic}` +
                    (aprox ? `\n· Incluidos aprox por match textual: ${aprox}` : "");
                  return (
                    <tr key={c.captura_id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 whitespace-nowrap text-slate-700">
                        <Calendar className="w-3 h-3 inline mr-1 text-slate-400" />
                        {fmtDate(c.started_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-medium">
                        <MapPin className="w-3 h-3 inline mr-1 text-slate-400" />
                        {c.zona_nombre || "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-500">
                        {c.trigger === "scheduler" ? "auto" : "manual"}
                      </td>
                      <td className="px-4 py-3 text-right font-mono cursor-help" title={lossBreakdown}
                          data-testid={`raw-util-${c.captura_id}`}>
                        {totalRaw} · {totalUtil}
                        {totalRaw > 0 && totalUtil === 0 && (
                          <div className="mt-0.5 text-[10px] font-normal text-amber-700">
                            ⚠ 0 en zona — pasá el mouse
                          </div>
                        )}
                        {(outside > 0 || sinUbic > 0) && totalUtil > 0 && (
                          <div className="mt-0.5 text-[10px] font-normal text-slate-400">
                            {outside > 0 && `${outside} fuera`}{outside > 0 && sinUbic > 0 && " · "}{sinUbic > 0 && `${sinUbic} sin ubic`}
                            {aprox > 0 && ` · ${aprox} aprox`}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{fmtPct(yieldAvg)}</td>
                      <td className="px-4 py-3 text-right font-mono text-slate-500">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {dur != null ? `${dur}s` : "—"}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{fmtUSD(r.costo_total_usd)}</td>
                      <td className="px-4 py-3 text-right font-mono text-slate-500">{r.cross_source_dupes ?? 0}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 border ${st.cls}`}>
                            <StIcon className={`w-3 h-3 ${st.spin ? "animate-spin" : ""}`} />
                            {st.label}
                          </span>
                          {c.status === "en_curso" && (
                            <button
                              type="button"
                              onClick={() => cancelCaptura(c.captura_id)}
                              disabled={cancellingId === c.captura_id}
                              data-testid={`cancel-captura-${c.captura_id}`}
                              title="Aborta el/los run(s) de Apify y registra el costo hasta el abort"
                              className="flex items-center gap-1 px-2 py-0.5 border border-red-300 bg-white text-red-700 font-heading font-bold uppercase text-[10px] tracking-wider hover:bg-red-50 hover:border-red-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              {cancellingId === c.captura_id ? (
                                <><Loader2 className="w-3 h-3 animate-spin" /> …</>
                              ) : (
                                <><X className="w-3 h-3" /> Cancelar</>
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
  );
}

function KpiCard({ icon, label, value, testid }) {
  return (
    <div className="bg-white border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-slate-500 mb-1.5">
        {icon}
        {label}
      </div>
      <div className="font-heading text-2xl font-black text-slate-900">{value}</div>
    </div>
  );
}
